import type { ComponentPropsWithRef, ReactNode } from 'react'
import React, { forwardRef, useEffect, useState } from 'react'

import { AccordionContentDrawer } from '../AccordionContentDrawer'

export const AccordionItem = forwardRef<HTMLElement, AccordionItemProps>(
  (
    {
      navItem,
      renderOpen = false,
      className = '',
      disabled,
      label,
      content,
      callback,
      animatesOpen = true,
      tag = 'section',
      ...rest
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(
      renderOpen || disabled === false
    )

    useEffect(() => {
      setIsOpen(renderOpen)
    }, [setIsOpen, renderOpen])

    useEffect(() => {
      if (disabled && isOpen) {
        setIsOpen(false)
      }
    }, [setIsOpen, disabled, isOpen])

    return (
      <AccordionContentDrawer
        {...{
          tag,
          navItem,
          className,
          disabled,
          label,
          content,
          callback,
          animatesOpen,
          setIsOpen,
          isOpen,
          ...rest,
        }}
        ref={ref}
      />
    )
  }
)

///////// IMPLEMENTATION /////////

export type AccordionItemBaseProps = {
  /**
   * Aria label to be read aloud by screen readers, usually the text inside label
   */
  ariaLabel?: string
  /**
   * Label that appears on the accordion item button. This can be plain text and/or a child elements if needed.
   */
  label: ReactNode
  /**
   * Content that will be displayed when the accordion item button is clicked on. This can be plain text and/or a child elements if needed.
   */
  content: ReactNode
  /**
   * Class name to be added to accordion item for additional styling.
   */
  className?: string
  /**
   * Whether an accordion item renders with or without Local Nav styling.
   */
  navItem?: boolean
  /**
   * When rendering multiple AccordionItems, it allows the user to have only
   * one accordionItem open at a time, by closing the last opened one when next one is opened
   * This avoids having a large scrollable content on the UI
   */
  disabled?: boolean
  /**
   * callback that will be fired when the accordionItem drawer is clicked
   */
  callback?: (isOpen: boolean) => void
  /**
   * Dictates if accordion item animates open and closed
   */
  animatesOpen?: boolean
  /**
   * Type of tag.
   */
  tag?: keyof JSX.IntrinsicElements
} & Omit<ComponentPropsWithRef<'section'>, 'content'>

export type AccordionItemProps = AccordionItemBaseProps & {
  /**
   * Whether the component's child elements are displayed when the component is rendered. Default value is false.
   */
  renderOpen?: boolean
}
